import { computed, CSSProperties, WritableComputedRef, ComputedRef, Ref } from 'vue';
import { isArray, isUndefined } from 'lodash-es';
import { VirtualItem } from '@tanstack/vue-virtual';
import { IconType } from '@/types/icon.types';
import { useColumnPinning } from '../column/useColumnPinning';
import type { Props } from '../../modernGrid.setup';
import type {
  GridRawRowDataType,
  GridTableInstance,
  GridRowInstance,
  GridCellInstance,
  GridColumnInstance,
  GridStyleOption,
  GridRowSelectionOption,
  GridTreeViewOption,
  GridDetailRowOption,
  GridGroupingOption,
  GridColumnPinningOption,
  GridExpandingOption,
  GridDynamicStyleOption,
  GridColumnMeta,
} from '../../modernGrid.types';
import { TREE_INDENT_SIZE } from '../../modernGrid.define';
import { matchesCondition } from '../../modernGrid.utils';

export const useBodyTemplate = <RowDataType extends GridRawRowDataType>({
  props,
  table,
  selectedRowId,
  clickedRowId,
  rowSelectionInfo,
  highlightedRowIds,
  treeViewInfo,
  detailRowInfo,
  groupingInfo,
  columnPinningInfo,
  expandingInfo,
  expandingColumn,
  styleInfo,
}: {
  props: Props<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  selectedRowId: WritableComputedRef<string>;
  clickedRowId: Ref<string>;
  rowSelectionInfo: ComputedRef<GridRowSelectionOption>;
  highlightedRowIds: ComputedRef<string[]>;
  treeViewInfo: ComputedRef<GridTreeViewOption>;
  detailRowInfo: ComputedRef<GridDetailRowOption>;
  groupingInfo: ComputedRef<GridGroupingOption>;
  columnPinningInfo: ComputedRef<GridColumnPinningOption>;
  expandingInfo: ComputedRef<GridExpandingOption>;
  expandingColumn: Ref<string>;
  styleInfo: ComputedRef<GridStyleOption>;
}) => {
  const { getPinnedCellStyle, getPinnedClasses } = useColumnPinning<RowDataType>({
    table,
    columnPinningInfo,
  });

  const getDynamicStyle = (
    dynamicStyles: GridDynamicStyleOption[] | undefined,
    instance: GridCellInstance<RowDataType> | GridRowInstance<RowDataType>,
  ): CSSProperties => {
    if (!dynamicStyles?.length) {
      return {};
    }

    const dynamicStyle = dynamicStyles.reduceRight<GridDynamicStyleOption | null>(
      (acc, option) => (matchesCondition(option, instance) ? option : acc),
      null,
    );

    return dynamicStyle?.style || {};
  };

  const createDynamicStyle = (style: CSSProperties): CSSProperties => ({
    ...(style?.backgroundColor && { backgroundColor: style.backgroundColor }),
    ...(style?.color && { '--dynamic-color': style.color }),
  });

  const getBodyCellStyle = (
    cell: GridCellInstance<RowDataType>,
    cells: GridCellInstance<RowDataType>[],
  ): CSSProperties => {
    const baseStyle: CSSProperties = {
      width: `${cell.column.getSize()}px`,
    };

    const dynamicStyle = getDynamicStyle(props.dynamicStyles, cell);
    const pinnedStyle = getPinnedCellStyle(cell, cells) || {};

    return {
      ...baseStyle,
      ...pinnedStyle,
      ...createDynamicStyle(dynamicStyle),
    };
  };

  const getBodyCellClasses = (cell: GridCellInstance<RowDataType>): any[] => {
    if (!cell || !cell.column) {
      return ['modern-grid-body__cell'];
    }
    const column = cell.column as GridColumnInstance<RowDataType>;
    const { row } = cell;
    const columnId = column.id;
    const { align, rendererType, type } = column.columnDef.meta || {};
    const isDetailRow = row.original.isDetail;
    let alignClass: string | undefined;

    if (rendererType === 'ratio' || type === 'boolean') {
      alignClass = 'center-align';
    } else if (isDetailRow) {
      alignClass = 'left-align';
    } else if (type === 'number' || type === 'float' || type === 'age') {
      alignClass = 'right-align';
    } else if (align) {
      alignClass = `${align}-align`;
    }
    if (row.getCanExpand() && columnId === expandingColumn.value) {
      alignClass = 'left-align';
    }

    if (rendererType === 'input-cell' && props.isChangedCell?.(row.original, columnId)) {
      alignClass = `${alignClass ?? ''} modern-grid-body__cell--changed`;
    }

    return [
      'modern-grid-body__cell',
      alignClass,
      {
        'is-resizing': column.getIsResizing(),
        'modern-grid-body__cell--detail': isDetailRow,
        [columnId]: !isDetailRow,
        ...(!isDetailRow && getPinnedClasses(cell)),
      },
    ].filter(Boolean);
  };

  const getBodyRowAttributes = (row: GridRowInstance<RowDataType>, index: number) => {
    const rowId = row.id;
    const isSelected = selectedRowId.value === rowId;
    const isChecked = rowSelectionInfo.value.useCheckbox ? row.getIsSelected() : false;
    const isExpanded =
      treeViewInfo.value.use || detailRowInfo.value.use ? row.getIsExpanded() : false;
    const isHighlighted = highlightedRowIds.value.includes(rowId);
    const isDisabled = !row.getCanSelect();

    const rowOriginal = row.original as Record<string, unknown> | undefined;
    const parentId = rowOriginal?.parentId as string | undefined;
    const grandparentId = rowOriginal?.grandparentId as string | undefined;

    return {
      'data-index': index,
      'data-original-index': row.index,
      'data-id': rowId,
      'data-selected': isSelected,
      'data-checked': isChecked,
      'data-expanded': isExpanded,
      'data-highlighted': isHighlighted,
      'data-disabled': isDisabled,
      'data-depth': row.depth,
      ...(parentId != null && parentId !== '' && { 'data-parent-id': String(parentId) }),
      ...(grandparentId != null &&
        grandparentId !== '' && { 'data-grandparent-id': String(grandparentId) }),
    };
  };

  const getBodyRowDynamicStyle = (row: GridRowInstance<RowDataType>): CSSProperties => {
    const dynamicStyle = getDynamicStyle(props.dynamicStyles, row);

    return {
      ...createDynamicStyle(dynamicStyle),
    };
  };

  const getBodyRowVirtualStyle = (
    row: GridRowInstance<RowDataType>,
    virtualRow: VirtualItem,
    hasDetailSlot: boolean,
  ): CSSProperties => {
    const isDetailRow =
      detailRowInfo.value.use && (row.original.isDetail ?? row.depth > 0) && hasDetailSlot;

    const getRowHeightStyle = (): CSSProperties => {
      if (isDetailRow) {
        // detailRowHeight가 설정되어 있을 때만 높이 고정
        if (detailRowInfo.value.detailRowHeight != null) {
          return {
            height: `${detailRowInfo.value.detailRowHeight}px`,
          };
        }
        return {};
      }
      return {
        minHeight: `${styleInfo.value.body?.rowMinHeight}px`,
        height: `${virtualRow.size}px`,
      };
    };

    return {
      transform: `translateY(${virtualRow.start}px)`,
      ...getRowHeightStyle(),
      ...getBodyRowDynamicStyle(row),
    };
  };

  const getBodyRowClasses = (row: GridRowInstance<RowDataType>): any[] => {
    const { rowId } = row.original;
    const rowOriginal = row.original as Record<string, unknown>;
    const isDisabled = props.disabledRows?.some((disabledRow) => {
      const disabledObj = disabledRow as Record<string, unknown>;
      const keys = Object.keys(disabledObj).filter((k) => k !== 'rowId');

      return keys.every((key) => {
        const rowValue = rowOriginal[key];
        const disabledValue = disabledObj[key];

        if (Array.isArray(rowValue) && Array.isArray(disabledValue)) {
          return JSON.stringify(rowValue) === JSON.stringify(disabledValue);
        }

        return rowValue === disabledValue;
      });
    });
    const isHighlighted = highlightedRowIds.value.includes(rowId);
    // selectedRowId가 설정되어 있으면 그것을 우선하고, 없으면 clickedRowId를 확인
    const isSelected = selectedRowId.value
      ? selectedRowId.value === rowId
      : clickedRowId.value === rowId;
    const isExpandable = treeViewInfo.value.use || detailRowInfo.value.use;
    let expandClass = '';

    if (isExpandable) {
      expandClass = row.getIsExpanded() ? 'expanded' : 'collapsed';
    }

    return [
      'modern-grid-body__row',
      expandClass,
      {
        'modern-grid-body__row--disabled': isDisabled,
        'modern-grid-body__row--highlighted': isHighlighted,
        'modern-grid-body__row--selected': isSelected,
      },
    ].filter(Boolean);
  };

  const getBodyCellPaddingLeft = (cell: GridCellInstance<RowDataType>): string | number => {
    const treeIndentSize = styleInfo.value.body?.treeIndentSize || TREE_INDENT_SIZE;
    return expandingColumn.value === cell.column.id ? `${treeIndentSize * cell.row.depth}px` : 0;
  };

  const hasExpandableRows = computed<boolean>(() => {
    if (!treeViewInfo.value.use) {
      return false;
    }
    const rows = table.value?.getRowModel().flatRows || [];
    return rows.some((row) => row.getCanExpand());
  });

  const getBodyExpandBtnIcon = (row: GridRowInstance<RowDataType>): IconType =>
    row.getIsExpanded()
      ? (expandingInfo.value.expandedIcon as IconType)
      : (expandingInfo.value.collapsedIcon as IconType);

  const getCellValue = (cell: GridCellInstance<RowDataType>): any => cell.getValue();

  const getIsDefaultCell = (cell: GridCellInstance<RowDataType>): boolean => {
    const { column, getValue } = cell;
    const { rendererType, clickable } = (column.columnDef.meta as GridColumnMeta) || {};
    return (
      !rendererType ||
      !clickable ||
      (rendererType === 'chip-cell' && !isArray(getValue())) ||
      (rendererType === 'chip-cell-clickable' && !isArray(getValue()))
    );
  };

  const getRenderingValue = (cell: GridCellInstance<RowDataType>): any => {
    const { row, column, getValue } = cell;
    const groupingColumnId = row.groupingColumnId || '';
    const isGroupedRow = row.getIsGrouped();
    const valueByGroupingColumn = row.original[groupingColumnId];

    let result: any = getValue();
    const isExpandColumn = expandingColumn.value === column.id;
    if (isGroupedRow && isExpandColumn) {
      result = isUndefined(groupingInfo.value.getGroupingValue)
        ? valueByGroupingColumn
        : groupingInfo.value.getGroupingValue(row);
    } else if (isGroupedRow && !isExpandColumn) {
      result = '';
    }

    if (isArray(result) && result.length === 0) {
      return '';
    }
    return result;
  };

  return {
    getBodyCellStyle,
    getBodyCellClasses,
    getBodyRowAttributes,
    getBodyRowDynamicStyle,
    getBodyRowVirtualStyle,
    getBodyRowClasses,
    getBodyCellPaddingLeft,
    hasExpandableRows,
    getBodyExpandBtnIcon,
    getCellValue,
    getIsDefaultCell,
    getRenderingValue,
  };
};
